import React, { useEffect, useState, useCallback } from 'react';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { ColumnsType } from 'antd/es/table';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';
import { notificationController } from 'controllers/notificationController';
import { Status } from '@app/components/profile/profileCard/profileFormNav/nav/payments/paymentHistory/Status/Status';
import { useMounted } from '@app/hooks/useMounted';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { getAllPushNotificationsAction, sendPushNotificationAction } from '@app/store/slices/userSlice';

interface ChoosePushNotificationTableProps {
  selectedDeliveries: any[];
  onDrawerClose: () => void;
  fetchDeliveriesData: () => void;
  handleCancelBulkNotification: () => void;
}

const initialPagination = {
  current: 1,
  pageSize: 20,
};

export const ChoosePushNotificationTable: React.FC<ChoosePushNotificationTableProps> = ({
  selectedDeliveries,
  onDrawerClose,
  fetchDeliveriesData,
  handleCancelBulkNotification
}) => {
  const [tableData, setTableData] = useState<{ data: any[]; pagination: any; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const [sendingNotifications, setSendingNotifications] = useState<{ [key: string]: boolean }>({});
  
  const { t } = useTranslation();
  const { isMounted } = useMounted();
  const dispatch = useAppDispatch();

  useEffect(() => {
    setTableData((prev) => ({ ...prev, loading: true }));
    dispatch(getAllPushNotificationsAction({}))
      .unwrap()
      .then((resp) => {
        setTableData({ 
          data: resp.data || [], 
          pagination: {
            current: resp.page || 1,
            pageSize: resp.pageSize || 20,
            total: resp.total || 0
          },
          loading: false 
        });
      }).catch((err) => {
        setTableData((prev) => ({ ...prev, loading: false }));
        notificationController.error({ message: err.message });
      })
  }, []);

  const fetch = useCallback(
    (pagination: any) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize
      };
      dispatch(getAllPushNotificationsAction(params))
        .unwrap()
        .then((resp) => {
          if (isMounted.current) {
            setTableData({ 
              data: resp.data || [], 
              pagination: {
                current: resp.page || 1,
                pageSize: resp.pageSize || 20,
                total: resp.total || 0
              },
              loading: false 
            });
          }
        }).catch((err) => {
          if (isMounted.current) {
            setTableData((prev) => ({ ...prev, loading: false }));
            notificationController.error({ message: err.message });
          }
        });
    },
    [isMounted, dispatch],
  );

  const handleTableChange = (pagination: any) => {
    fetch(pagination);
  };

  const handleSendNotification = (pushNotification: any) => {
    setSendingNotifications(prev => ({ ...prev, [pushNotification._id]: true }));
    
    const deliveryIds = selectedDeliveries.map(delivery => delivery._id);
    const clientIds = selectedDeliveries.map(delivery => delivery.client?._id).filter(Boolean);
    
    const payload = {
      template_id: pushNotification._id,
      title: pushNotification.title,
      description: pushNotification.description,
      deliveryIds,
      clientIds,
      templateType: pushNotification.templateType,
      isTemplate: pushNotification.isTemplate
    };

    dispatch(sendPushNotificationAction(payload))
      .unwrap()
      .then((resp) => {
        setSendingNotifications(prev => ({ ...prev, [pushNotification._id]: false }));
        if (resp.success) {
          notificationController.success({ 
            message: `Push notification "${pushNotification.title}" sent successfully to ${selectedDeliveries.length} deliveries!` 
          });
          onDrawerClose();
          handleCancelBulkNotification();
          fetchDeliveriesData();
        } else {
          notificationController.error({ message: resp.message || 'Failed to send push notification' });
        }
      })
      .catch((err) => {
        setSendingNotifications(prev => ({ ...prev, [pushNotification._id]: false }));
        notificationController.error({ message: err.message || 'Failed to send push notification' });
      });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text: string) => <span>{text?.substring(0, 100)}{text?.length > 100 ? '...' : ''}</span>,
    },
    {
      title: 'Template Type',
      dataIndex: 'templateType',
      render: (text: string) => <span>{text || 'N/A'}</span>,
    },
    {
      title: 'Is Template',
      key: 'isTemplate',
      dataIndex: 'isTemplate',
      render: (isTemplate: boolean) => (
        <BaseRow gutter={[10, 10]}>
          <BaseCol>
            <Status color={isTemplate ? 'var(--success-color)' : 'var(--error-color)'} text={isTemplate ? 'YES' : 'NO'} />
          </BaseCol>
        </BaseRow>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => {
        const isLoading = sendingNotifications[record._id] || false;
        return (
          <BaseSpace>
            <BaseButton
              type="primary"
              loading={isLoading}
              onClick={() => handleSendNotification(record)}
              disabled={!selectedDeliveries.length}
            >
              {isLoading ? 'Sending...' : 'Send'}
            </BaseButton>
          </BaseSpace>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <h3>Choose Push Notification</h3>
        <p>Selected {selectedDeliveries.length} deliveries for notification</p>
        <BaseButton 
          onClick={handleCancelBulkNotification}
          style={{ marginBottom: '10px' }}
        >
          Cancel
        </BaseButton>
      </div>
      
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={tableData.pagination}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
    </>
  );
};
